import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Save, Sliders, Cpu, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

// ── Available providers & models ──────────────────────────────────────

const PROVIDERS: Record<string, { value: string; label: string }[]> = {
  gemini: [
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  ],
  anthropic: [
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    { value: 'claude-sonnet-4-5-20250514', label: 'Claude Sonnet 4.5' },
  ],
};

const PROVIDER_LABELS: Record<string, string> = {
  gemini: 'Gemini',
  anthropic: 'Anthropic',
};

// ── Types ──────────────────────────────────────────────────────────────

interface LLMConfig {
  function_key: string;
  provider: string;
  model: string;
  max_tokens: number | null;
  temperature: number | null;
  is_active: boolean;
  notes: string | null;
  updated_at: string;
}

interface EditState {
  provider: string;
  model: string;
  max_tokens: string;
  temperature: string;
  is_active: boolean;
}

// ── Component ──────────────────────────────────────────────────────────

export default function AdminLLMConfig() {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState<LLMConfig[]>([]);
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('llm_model_config')
        .select('*')
        .order('function_key');

      if (error) throw error;
      setConfigs((data as LLMConfig[]) || []);
      setEdits({});
    } catch (err) {
      console.error('Failed to fetch LLM configs:', err);
      toast.error('Failed to load LLM configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  // ── Edit helpers ──────────────────────────────────────────────────

  const getEditState = (config: LLMConfig): EditState => {
    if (edits[config.function_key]) return edits[config.function_key];
    return {
      provider: config.provider,
      model: config.model,
      max_tokens: config.max_tokens?.toString() ?? '',
      temperature: config.temperature?.toString() ?? '',
      is_active: config.is_active,
    };
  };

  const setEdit = (key: string, field: keyof EditState, value: string | boolean) => {
    setEdits(prev => {
      const config = configs.find(c => c.function_key === key)!;
      const current = prev[key] ?? {
        provider: config.provider,
        model: config.model,
        max_tokens: config.max_tokens?.toString() ?? '',
        temperature: config.temperature?.toString() ?? '',
        is_active: config.is_active,
      };

      const updated = { ...current, [field]: value };

      // Reset model when provider changes
      if (field === 'provider' && typeof value === 'string') {
        const models = PROVIDERS[value];
        if (models && !models.some(m => m.value === current.model)) {
          updated.model = models[0].value;
        }
      }

      return { ...prev, [key]: updated };
    });
  };

  const isDirty = (config: LLMConfig): boolean => {
    const edit = edits[config.function_key];
    if (!edit) return false;
    return (
      edit.provider !== config.provider ||
      edit.model !== config.model ||
      edit.max_tokens !== (config.max_tokens?.toString() ?? '') ||
      edit.temperature !== (config.temperature?.toString() ?? '') ||
      edit.is_active !== config.is_active
    );
  };

  const saveConfig = async (key: string) => {
    const edit = edits[key];
    if (!edit) return;

    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const { error } = await supabase
        .from('llm_model_config')
        .update({
          provider: edit.provider,
          model: edit.model,
          max_tokens: edit.max_tokens ? parseInt(edit.max_tokens) : null,
          temperature: edit.temperature ? parseFloat(edit.temperature) : null,
          is_active: edit.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('function_key', key);

      if (error) throw error;

      toast.success(`Updated ${key}`);
      await fetchConfigs();
    } catch (err) {
      console.error('Failed to save config:', err);
      toast.error(`Failed to update ${key}`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  // ── KPI aggregation ───────────────────────────────────────────────

  const totalConfigs = configs.length;
  const geminiCount = configs.filter(c => c.provider === 'gemini').length;
  const anthropicCount = configs.filter(c => c.provider === 'anthropic').length;
  const activeCount = configs.filter(c => c.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">LLM Model Configuration</h1>
          <p className="text-muted-foreground">Manage which AI provider and model each function uses</p>
        </div>
        <Button onClick={fetchConfigs} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Sliders className="h-4 w-4" /> Total Functions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{totalConfigs}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Gemini
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{geminiCount}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Cpu className="h-4 w-4" /> Anthropic
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{anthropicCount}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Sliders className="h-4 w-4" /> Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-2xl font-bold">{activeCount}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Config Table */}
      <Card>
        <CardHeader>
          <CardTitle>Function Model Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Function</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Model</TableHead>
                <TableHead className="w-[100px]">Max Tokens</TableHead>
                <TableHead className="w-[90px]">Temp</TableHead>
                <TableHead className="w-[70px]">Active</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No configurations found. Run the database migration to seed defaults.
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => {
                  const edit = getEditState(config);
                  const dirty = isDirty(config);
                  const isSaving = saving[config.function_key] ?? false;
                  const availableModels = PROVIDERS[edit.provider] ?? [];

                  return (
                    <TableRow key={config.function_key} className={dirty ? 'bg-primary/5' : undefined}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {config.function_key}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={edit.provider}
                          onValueChange={(v) => setEdit(config.function_key, 'provider', v)}
                        >
                          <SelectTrigger className="w-[120px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(PROVIDER_LABELS).map(([key, label]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={edit.model}
                          onValueChange={(v) => setEdit(config.function_key, 'model', v)}
                        >
                          <SelectTrigger className="w-[200px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableModels.map((m) => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          placeholder="default"
                          className="w-[100px] h-8 text-xs"
                          value={edit.max_tokens}
                          onChange={(e) => setEdit(config.function_key, 'max_tokens', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="2"
                          placeholder="default"
                          className="w-[80px] h-8 text-xs"
                          value={edit.temperature}
                          onChange={(e) => setEdit(config.function_key, 'temperature', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={edit.is_active}
                          onCheckedChange={(v) => setEdit(config.function_key, 'is_active', v)}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate" title={config.notes ?? ''}>
                        {config.notes || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(new Date(config.updated_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell>
                        {dirty && (
                          <Button
                            size="sm"
                            variant="default"
                            className="h-7 text-xs"
                            disabled={isSaving}
                            onClick={() => saveConfig(config.function_key)}
                          >
                            {isSaving ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <Save className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
